import React, { useState, useEffect } from "react";
import {
  Form,
  Slider,
  Card,
  Typography,
  Alert,
  Table,
  Select,
  Space,
  Radio,
  InputNumber,
} from "antd";
import {
  ClockCircleOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

// Predefined intervals in minutes
const PREDEFINED_INTERVALS = [1, 2, 3, 5, 7, 10, 15, 20, 30];

const EmailVolumeControl = ({
  totalRecipients,
  initialMinutesBetweenEmails = 5,
  customInterval: initialCustomInterval = false,
  emailsPerHour: initialEmailsPerHour = 12,
  onVolumeChange,
  senderCount = 1,
  initialSendingMode = "time_based",
  initialEmailsPerMinute = 20,
}) => {
  const [minutesBetweenEmails, setMinutesBetweenEmails] = useState(
    initialMinutesBetweenEmails
  );
  const [customInterval, setCustomInterval] = useState(initialCustomInterval);
  const [sendingMode, setSendingMode] = useState(initialSendingMode);
  const [emailsPerMinute, setEmailsPerMinute] = useState(
    initialEmailsPerMinute
  );

  // Enhanced calculations for multiple senders
  const emailsPerHour = Math.floor(60 / minutesBetweenEmails);
  const secondsBetweenEmails = minutesBetweenEmails * 60;
  const effectiveSenderCount = Math.max(1, senderCount);

  // Calculate emails per sender and distribution
  const emailsPerSender = Math.ceil(totalRecipients / effectiveSenderCount);
  const lastSenderEmails =
    totalRecipients - emailsPerSender * (effectiveSenderCount - 1);

  // Calculate base metrics for time-based sending
  const calculateTimeBasedMetrics = () => {
    if (sendingMode === "time_based") {
      // Each sender sends 1 email every X minutes
      const emailsPerSenderPerInterval = 1;
      const totalEmailsPerInterval =
        effectiveSenderCount * emailsPerSenderPerInterval;

      // Calculate how many intervals we need
      const totalIntervals = Math.ceil(
        totalRecipients / totalEmailsPerInterval
      );

      // Calculate total minutes needed
      const totalMinutes = totalIntervals * minutesBetweenEmails;

      // Calculate hours and remaining minutes
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;

      // Calculate emails per hour
      const emailsPerHour = Math.floor(
        (60 / minutesBetweenEmails) * effectiveSenderCount
      );

      // Calculate distribution per sender
      const baseEmailsPerSender = Math.floor(
        totalRecipients / effectiveSenderCount
      );
      const remainingEmails = totalRecipients % effectiveSenderCount;

      const senderDistribution = Array.from(
        { length: effectiveSenderCount },
        (_, index) => {
          const extraEmail = index < remainingEmails ? 1 : 0;
          const senderEmails = baseEmailsPerSender + extraEmail;
          const senderIntervals = Math.ceil(senderEmails);
          const senderMinutes = senderIntervals * minutesBetweenEmails;
          const senderHours = Math.floor(senderMinutes / 60);
          const senderRemainingMinutes = senderMinutes % 60;

          return {
            senderIndex: index + 1,
            emails: senderEmails,
            duration: senderMinutes,
            emailsPerHour: Math.floor(60 / minutesBetweenEmails),
            displayDuration:
              senderHours > 0
                ? `${senderHours}.${Math.floor(senderRemainingMinutes / 6)}` // Convert minutes to decimal hours
                : `${senderMinutes} minutes`,
          };
        }
      );

      return {
        totalMinutes,
        hours,
        remainingMinutes,
        emailsPerHour,
        senderDistribution,
        totalIntervals,
      };
    }
    return null;
  };

  const timeBasedMetrics = calculateTimeBasedMetrics();

  // Calculate timing for each sender
  const sendersTimings =
    sendingMode === "time_based"
      ? timeBasedMetrics.senderDistribution
      : Array.from({ length: effectiveSenderCount }, (_, index) => {
          // Rate-based calculations remain the same
          const isLastSender = index === effectiveSenderCount - 1;
          const senderEmails = isLastSender
            ? lastSenderEmails
            : emailsPerSender;
          const totalCapacity = emailsPerMinute * effectiveSenderCount;
          return {
            senderIndex: index + 1,
            emails: senderEmails,
            duration: Math.ceil(
              senderEmails / (totalCapacity / effectiveSenderCount)
            ),
            emailsPerMinute: emailsPerMinute,
          };
        });

  // Calculate overall campaign metrics
  const totalSendingCapacityPerMinute =
    sendingMode === "rate_based"
      ? emailsPerMinute * effectiveSenderCount
      : timeBasedMetrics
      ? timeBasedMetrics.emailsPerHour / 60
      : 0;

  // Calculate campaign duration
  const campaignDurationMinutes = Math.ceil(
    totalRecipients / totalSendingCapacityPerMinute
  );
  const campaignDurationHours = campaignDurationMinutes / 60;

  // Update parent component
  useEffect(() => {
    if (onVolumeChange) {
      onVolumeChange({
        minutesBetweenEmails,
        emailsPerHour:
          sendingMode === "rate_based"
            ? emailsPerMinute * effectiveSenderCount * 60
            : timeBasedMetrics
            ? timeBasedMetrics.emailsPerHour
            : 0,
        customInterval,
        totalDuration:
          sendingMode === "rate_based"
            ? campaignDurationMinutes
            : timeBasedMetrics
            ? timeBasedMetrics.totalMinutes
            : 0,
        sendingPattern: "fixed_interval",
        senderDistribution: sendersTimings,
        sendingMode,
        emailsPerMinute:
          sendingMode === "rate_based" ? emailsPerMinute : undefined,
        sendingRate:
          sendingMode === "rate_based"
            ? {
                configured: emailsPerMinute,
                totalSenders: effectiveSenderCount,
                effectiveRate: emailsPerMinute * effectiveSenderCount,
              }
            : undefined,
      });
    }
  }, [
    minutesBetweenEmails,
    customInterval,
    campaignDurationMinutes,
    senderCount,
    sendingMode,
    emailsPerMinute,
  ]);

  const handleIntervalChange = (value) => {
    if (value === "custom") {
      setCustomInterval(true);
    } else {
      setMinutesBetweenEmails(Number(value));
      setCustomInterval(false);
    }
  };

  const handleCustomSliderChange = (value) => {
    setMinutesBetweenEmails(value);
  };

  const handleSendingModeChange = (e) => {
    setSendingMode(e.target.value);
  };

  const handleEmailsPerMinuteChange = (value) => {
    setEmailsPerMinute(value);
  };

  // Update the alert message for rate-based sending
  const getRateBasedAlertMessage = () => {
    const totalCapacity = emailsPerMinute * effectiveSenderCount;
    return `Your campaign has ${totalRecipients} recipients. With ${effectiveSenderCount} senders at ${emailsPerMinute} emails/minute each (total capacity: ${totalCapacity} emails/minute), the campaign will take approximately ${campaignDurationMinutes} minutes to complete.`;
  };

  // Sender distribution table columns
  const senderColumns = [
    {
      title: "Sender",
      dataIndex: "senderIndex",
      key: "senderIndex",
      render: (text) => `Sender ${text}`,
    },
    {
      title: "Emails",
      dataIndex: "emails",
      key: "emails",
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (minutes) =>
        minutes >= 60
          ? `${(minutes / 60).toFixed(1)} hours`
          : `${minutes} minutes`,
    },
    {
      title: sendingMode === "rate_based" ? "Emails/Minute" : "Emails/Hour",
      dataIndex:
        sendingMode === "rate_based" ? "emailsPerMinute" : "emailsPerHour",
      key: "rate",
      render: (value) => value.toFixed(1),
    },
  ];

  // Update the summary message
  const getSummaryMessage = () => {
    if (sendingMode === "time_based") {
      const durationText =
        timeBasedMetrics.hours > 0
          ? `${timeBasedMetrics.hours} hours ${timeBasedMetrics.remainingMinutes} minutes`
          : `${timeBasedMetrics.totalMinutes} minutes`;
      return `Sending ${totalRecipients} emails with ${minutesBetweenEmails} minute between each email per sender. Total duration: ${durationText}`;
    } else {
      // For rate-based mode, show a different summary to avoid duplication
      const totalCapacity = emailsPerMinute * effectiveSenderCount;
      const hourlyRate = totalCapacity * 60;
      return `This campaign will send up to ${hourlyRate} emails per hour (${totalCapacity}/minute across ${effectiveSenderCount} senders). Estimated completion time: ${Math.floor(
        campaignDurationHours
      )} hours ${campaignDurationMinutes % 60} minutes.`;
    }
  };

  // Multiple Senders Alert description
  const getMultipleSendersDescription = () => {
    const hourlyRate =
      sendingMode === "rate_based"
        ? emailsPerMinute * effectiveSenderCount * 60
        : timeBasedMetrics
        ? timeBasedMetrics.emailsPerHour
        : 0;

    return (
      <Text>
        Your campaign will be distributed across {effectiveSenderCount}{" "}
        different email senders, allowing you to send approximately {hourlyRate}{" "}
        emails per hour.
      </Text>
    );
  };

  // Enhanced calculation data
  const calculationData = [
    {
      interval:
        sendingMode === "rate_based"
          ? "Emails Per Minute"
          : "Time Between Emails",
      emails:
        sendingMode === "rate_based" ? emailsPerMinute : minutesBetweenEmails,
      duration:
        sendingMode === "rate_based"
          ? "1 minute"
          : `${minutesBetweenEmails} minutes`,
      details:
        sendingMode === "rate_based"
          ? `${emailsPerMinute} emails per minute per sender (${
              emailsPerMinute * effectiveSenderCount
            } total)`
          : `${minutesBetweenEmails * 60} seconds between emails per sender`,
    },
    {
      interval: "Per Hour (All Senders)",
      emails:
        sendingMode === "rate_based"
          ? emailsPerMinute * effectiveSenderCount * 60
          : timeBasedMetrics
          ? timeBasedMetrics.emailsPerHour
          : 0,
      duration: "1 hour",
      details:
        sendingMode === "rate_based"
          ? `${emailsPerMinute} × ${effectiveSenderCount} senders × 60 minutes = ${
              emailsPerMinute * effectiveSenderCount * 60
            } emails per hour`
          : `${Math.floor(
              60 / minutesBetweenEmails
            )} × ${effectiveSenderCount} senders = ${
              timeBasedMetrics ? timeBasedMetrics.emailsPerHour : 0
            } emails per hour`,
    },
    {
      interval: "Full Campaign",
      emails: totalRecipients,
      duration:
        sendingMode === "rate_based"
          ? `${Math.floor(campaignDurationHours)} hours ${
              campaignDurationMinutes % 60
            } minutes`
          : timeBasedMetrics && timeBasedMetrics.hours > 0
          ? `${timeBasedMetrics.hours} hours ${timeBasedMetrics.remainingMinutes} minutes`
          : timeBasedMetrics
          ? `${timeBasedMetrics.totalMinutes} minutes`
          : "0 minutes",
      details: `Distributed across ${effectiveSenderCount} senders (${sendersTimings
        .map((t) => t.emails)
        .join(" + ")} emails)`,
    },
  ];

  return (
    <Card className="email-volume-control">
      <Title level={4} style={{ marginBottom: 24 }}>
        <Space>
          <ClockCircleOutlined />
          Small Batch Email Configuration
        </Space>
      </Title>
      <Text type="secondary">
        Configure how you want to send your batch of {totalRecipients} emails
        distributed across {effectiveSenderCount} senders
      </Text>

      {/* Sending Mode Selection */}
      <div style={{ marginTop: 24 }}>
        <Text>Select sending mode:</Text>
        <Radio.Group
          value={sendingMode}
          onChange={handleSendingModeChange}
          style={{ marginTop: 8 }}
        >
          <Radio.Button value="time_based">
            <Space>
              <ClockCircleOutlined />
              Time-Based (Minutes between emails)
            </Space>
          </Radio.Button>
          <Radio.Button value="rate_based">
            <Space>
              <ThunderboltOutlined />
              Rate-Based (Emails per minute)
            </Space>
          </Radio.Button>
        </Radio.Group>
      </div>

      {/* Time-Based Controls */}
      {sendingMode === "time_based" && (
        <>
          <div style={{ marginTop: 24 }}>
            <Text>Select minutes between each email (per sender):</Text>
            <Select
              style={{ width: "100%", marginTop: 8 }}
              value={
                customInterval ? "custom" : minutesBetweenEmails.toString()
              }
              onChange={handleIntervalChange}
            >
              {PREDEFINED_INTERVALS.map((interval) => (
                <Option key={interval} value={interval.toString()}>
                  {interval} minutes between emails
                </Option>
              ))}
              <Option value="custom">Custom Interval</Option>
            </Select>
          </div>

          {customInterval && (
            <div style={{ marginTop: 24 }}>
              <Text>Custom interval (1-60 minutes):</Text>
              <Slider
                min={1}
                max={60}
                value={minutesBetweenEmails}
                onChange={handleCustomSliderChange}
                marks={{
                  1: "1m",
                  15: "15m",
                  30: "30m",
                  60: "60m",
                }}
              />
            </div>
          )}
        </>
      )}

      {/* Rate-Based Controls */}
      {sendingMode === "rate_based" && (
        <div style={{ marginTop: 24 }}>
          <Alert
            className="enterprise-alert"
            message="Enterprise Email Server Required"
            description={
              <div>
                <Text>
                  Rate-based sending is designed for enterprise email servers
                  (G-Suite, Office 365, etc.) with higher sending limits:
                </Text>
                <ul className="server-limits-list">
                  <li>
                    Office 365: <span>30 emails per minute</span>
                  </li>
                  <li>
                    G-Suite: <span>20 emails per minute</span>
                  </li>
                  <li>
                    Exchange Online: <span>30 emails per minute</span>
                  </li>
                </ul>
                <div className="safe-limit-text">
                  We've set a safe limit of 20 emails per minute to ensure
                  compatibility with most enterprise servers.
                </div>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Text>Set emails per minute (max 20):</Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            value={emailsPerMinute}
            onChange={handleEmailsPerMinuteChange}
            options={Array.from({ length: 20 }, (_, i) => ({
              value: i + 1,
              label: `${i + 1} emails/minute`,
            }))}
          />
          <Alert
            style={{ marginTop: 8 }}
            message="Rate-Based Sending Configuration"
            description={<Text>{getRateBasedAlertMessage()}</Text>}
            type="info"
            showIcon
          />
          <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            This will be distributed across {effectiveSenderCount} senders
          </Text>
        </div>
      )}

      {/* Multiple Senders Alert */}
      {effectiveSenderCount > 1 && (
        <Alert
          style={{ marginTop: 24 }}
          message={
            <Text>
              Multiple Senders Detected ({effectiveSenderCount} senders)
            </Text>
          }
          description={getMultipleSendersDescription()}
          type="info"
          showIcon
        />
      )}

      {/* Sending Summary */}
      <Alert
        style={{ marginTop: 24 }}
        message={
          <Space>
            <InfoCircleOutlined />
            <Text>{getSummaryMessage()}</Text>
          </Space>
        }
        type="info"
        showIcon={false}
      />

      {/* Calculation Table */}
      <Table
        style={{ marginTop: 24 }}
        columns={[
          { title: "Time Interval", dataIndex: "interval", key: "interval" },
          { title: "Emails", dataIndex: "emails", key: "emails" },
          { title: "Duration", dataIndex: "duration", key: "duration" },
          { title: "Details", dataIndex: "details", key: "details" },
        ]}
        dataSource={calculationData}
        pagination={false}
        size="small"
      />

      {/* Sender Distribution Table */}
      {effectiveSenderCount > 1 && (
        <div style={{ marginTop: 24 }}>
          <Title level={5}>Sender Distribution</Title>
          <Table
            columns={senderColumns}
            dataSource={sendersTimings}
            pagination={false}
            size="small"
          />
        </div>
      )}
    </Card>
  );
};

export default EmailVolumeControl;
